import { Box, Tabs, Divider, Typography, Badge } from '@mui/material'
import {
  IconAdjustments,
  IconBookmark,
  IconStar,
  IconUser,
  IconCreditCard,
  IconLogout,
  IconMenu2,
  IconGift,
} from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import { useAuthHelper } from '../../../../hooks/useAuthHelper'
import BasicModal from '../../../../components/modal/BasicModal'
import {
  selectEater,
  setCurrentTab,
  setIsEditCancelledData,
  setIsProfileEdit,
  setIsUpdateData,
  fetchEaterRestaurantReviews,
  fetchEaterSavedRestaurants,
} from '../../../../store/profileSlice'
import useRudderStackAnalytics from '../../../../useRudderAnalytics'
import {
  ProfileTabInnerContainer,
  ProfileTabMainContainer,
  ProfileTabProfileBioContainer,
  ProfileTabTabComponent,
} from '../styled/ProfileTab.styled'
import Preferences from './Preferences'
import ProfileBio from './ProfileBio'
import ProfileReviewTab from './ProfileReviewTab'
import ProfileSavedRestaurants from './ProfileSavedRestaurants'
import ProfileSavedMenus from './ProfileSavedMenus'
// import InfluencerProfile from './InfluencerProfile'
import ProfileWallet from './ProfileWallet'
import ProfileRewards from './ProfileRewards'
import { getRewardsInbox } from '../../../../services/walletPayoutService'
import { selectIsInfluencer } from '../../../../store/influencer/contentCreator'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
)

const a11yProps = (index: number): { id: string; 'aria-controls': string } => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
})

const tabData = [
  { label: 'Wallet', icon: <IconCreditCard size={20} stroke={2} /> },
  { label: 'Bio', icon: <IconUser size={20} stroke={2} /> },
  // { label: 'Influencer Profile', icon: <IconStar size={20} stroke={2} /> },
  { label: 'Preferences', icon: <IconAdjustments size={20} stroke={2} /> },
  { label: 'Saved Restaurants', icon: <IconBookmark size={20} stroke={2} /> },
  { label: 'Saved Menus', icon: <IconMenu2 size={20} stroke={2} /> },
  { label: 'Reviews', icon: <IconStar size={20} stroke={2} /> },
  { label: 'Rewards', icon: <IconGift size={20} stroke={2} /> },
]

const ProfileTab: React.FC = () => {
  const { dispatch, theme } = useAppUtilities()
  const location = useLocation()
  const userDetail = useAppSelector(selectEater)
  const IsInfluencer = useAppSelector(selectIsInfluencer)
  const { logoutAuthUser } = useAuthHelper()
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  // Count of rewards ready to claim — drives the badge on the Rewards tab.
  const [claimableRewards, setClaimableRewards] = useState(0)

  useEffect(() => {
    const loadClaimable = async (): Promise<void> => {
      try {
        const inbox = await getRewardsInbox()
        setClaimableRewards(
          inbox.filter(
            (r) => r.status === 'ACTIVE' || r.status === 'REDEEMED_EMAIL',
          ).length,
        )
      } catch {
        setClaimableRewards(0)
      }
    }
    void loadClaimable()
  }, [])

  // Check for tab query param
  const searchParams = new URLSearchParams(location.search)
  const tabParam = searchParams.get('tab')
  const initialTabIndex = tabParam 
    ? tabData.findIndex(tab => tab.label.toLowerCase() === tabParam.toLowerCase())
    : 0
  const [value, setValue] = useState(initialTabIndex >= 0 ? initialTabIndex : 0)

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    updateTab(newValue)
  }

  useEffect(() => {
    updateTab(value)
  }, [])
  
  useEffect(() => {
    // Update tab when query param changes
    if (tabParam) {
      const tabIndex = tabData.findIndex(tab => tab.label.toLowerCase() === tabParam.toLowerCase())
      if (tabIndex >= 0 && tabIndex !== value) {
        setValue(tabIndex)
        updateTab(tabIndex)
      }
    }
  }, [tabParam])

  const updateTab = (value: number): void => {
    dispatch(setIsProfileEdit(false))
    dispatch(setIsUpdateData(false))
    dispatch(setIsEditCancelledData(false))
    dispatch(setCurrentTab(tabData[value]?.label || 'Reviews'))
    setValue(value)
    
    // Fetch reviews when Reviews tab is selected
    if (tabData[value]?.label === 'Reviews' && userDetail?.eaterId) {
      void dispatch(
        fetchEaterRestaurantReviews({
          eaterId: userDetail.eaterId,
          page: 0,
          limit: 20,
        }),
      )
    }
    // Fetch saved restaurants when Saved Restaurants tab is selected
    if (tabData[value]?.label === 'Saved Restaurants' && userDetail?.eaterId) {
      void dispatch(
        fetchEaterSavedRestaurants({
          eaterId: userDetail.eaterId,
        }),
      )
    }
  }

  const handleLogoutClick = (): void => {
    // Open confirmation modal
    setIsLogoutModalOpen(true)
  }

  const handleLogoutCancel = (): void => {
    // Close confirmation modal
    setIsLogoutModalOpen(false)
  }

  const handleLogoutConfirm = (): void => {
    // Close modal
    setIsLogoutModalOpen(false)

    // Reset analytics
    if (analytics && isAnalyticsReady) {
      analytics?.identify('', { isLoggedIn: false })
      analytics?.reset()
    }

    // Perform logout - this clears auth/session state and redirects to home
    // (or appropriate unauthenticated entry screen) via Auth0 logout
    logoutAuthUser()
  }

  return (
    <ProfileTabMainContainer>
      <ProfileTabInnerContainer>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          aria-label="scrollable force basic tabs example"
        >
          {tabData
            .filter(
              (tab) => !(!IsInfluencer && tab.label === 'Influencer Profile'),
            )
            .map((tab, index) => (
              <ProfileTabTabComponent
                key={index}
                icon={tab.icon}
                label={
                  tab.label === 'Rewards' && claimableRewards > 0 ? (
                    <Badge
                      badgeContent={claimableRewards}
                      color="error"
                      sx={{ '& .MuiBadge-badge': { right: -10, top: 2 } }}
                    >
                      Rewards
                    </Badge>
                  ) : (
                    tab.label
                  )
                }
                {...a11yProps(index)}
              />
            ))}
        </Tabs>
      </ProfileTabInnerContainer>
      <CustomTabPanel value={value} index={1}>
        <ProfileTabProfileBioContainer>
          <ProfileBio />
        </ProfileTabProfileBioContainer>
      </CustomTabPanel>
      {/* <CustomTabPanel value={value} index={1}> */}
      {/*  <ProfileTabProfileBioContainer> */}
      {/*    <InfluencerProfile /> */}
      {/*  </ProfileTabProfileBioContainer> */}
      {/* </CustomTabPanel> */}
      <CustomTabPanel value={value} index={2}>
        <Preferences />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <ProfileSavedRestaurants />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <ProfileSavedMenus />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={0}>
        <ProfileTabProfileBioContainer>
          <ProfileWallet />
        </ProfileTabProfileBioContainer>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={5}>
        <ProfileReviewTab />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={6}>
        <ProfileRewards />
      </CustomTabPanel>

      {/* Logout Action Row */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Box
          onClick={handleLogoutClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 2,
            cursor: 'pointer',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            },
          }}
        >
          <IconLogout
            size={24}
            stroke={2}
            color={theme.palette.error.main}
          />
          <Typography
            sx={{
              color: theme.palette.error.main,
              fontWeight: 500,
              fontSize: '16px',
            }}
          >
            Log out
          </Typography>
        </Box>
      </Box>

      {/* Logout Confirmation Modal */}
      <BasicModal
        open={isLogoutModalOpen}
        onCancel={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title="Log out of Resactly?"
        buttonName="Log out"
        cancelButtonName="Cancel"
        handleClose={handleLogoutCancel}
        deleteModal={true}
      >
        <Typography variant="subtitle1" color={theme.palette.grey[800]} my={2}>
          Are you sure you want to log out? You will need to sign in again to access your account.
        </Typography>
      </BasicModal>
    </ProfileTabMainContainer>
  )
}

export default ProfileTab
